import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import {
  faGlobe,
  faCaretDown,
  faCaretRight,
} from "@fortawesome/free-solid-svg-icons";
import { useI18next } from "gatsby-plugin-react-i18next";
import styled from "styled-components";
import { TEXT_FONTSIZE } from "../utilities/constants";
import { useSelector } from "react-redux";
import { RootState } from "../redux/reducers/rootReducer";

interface LanguageListProps {
  border?: string;
  color?: string;
  mainPage?: boolean;
}

const LanguageList: React.FC<LanguageListProps> = (props: LanguageListProps) => {
  const [showList, setShowList] = useState(false);
  const { languages, changeLanguage, language } = useI18next();

  let shopDetails = useSelector((state: RootState) => {
    return state.shop;
  });

  // due to development constraints, these languages are only partially supported
  const specialLanguages = ["jp", "zh", "vi", "pt", "ko"];
  
  // Special language options for 5 specific pages
  const specialLanguageRoutes = ["plans", "plan-info", "activation", "info-fill-up", "checkout"];
  const window = global.window;
  // check if route contains any of the special language routes
  const isSpecialLanguageRoute = typeof window === 'undefined' ? false : specialLanguageRoutes.some(route => window.location.href.includes(route));

  const check = (lang) => {
    if (props.mainPage) {
      return lang.toUpperCase();
    }
    if (lang === "en") {
      return "English";
    } else if (lang === "fr") {
      return "Français";
    } else if (lang === "sp") {
      return "Español";
    } else if (lang === "jp") {
      return "日本語";
    } else if (lang === "zh") {
      return "中文";
    } else if (lang === "vi") {
      return "Vietnamese";
    } else if (lang === "ko") {
      return "Korean";
    } else if (lang === "pt") {
      return "Português";
    }
  };

  useEffect(() => {
    if (language === "ko") {
      // for Korean, we use the Pretendard font.
      document.documentElement.style.setProperty("--primary-theme", "Pretendard");
    } else {
      document.documentElement.style.setProperty("--primary-theme", "Poppins");
    }

    if (isSpecialLanguageRoute) {
      // would ideally use a switch statement here, but the 'language' const can only refer to the current language.
      if (window.location.href.includes("/jp/")) {
        changeLanguage("jp");
      } else if (window.location.href.includes("/zh/")) {
        changeLanguage("zh");
      } else if (window.location.href.includes("/vi/")) {
        changeLanguage("vi");
      } else if (window.location.href.includes("/ko/")) {
        changeLanguage("ko");
      } else if (window.location.href.includes("/pt/")) {
        changeLanguage("pt");
      }

    } else {
      // on non-special routes, we change the language back to english.
      if (specialLanguages.includes(language)) {
        changeLanguage("en");
      }
    }
  }, [isSpecialLanguageRoute, language]);

  return (
    <>
      <Container show={showList} border={props.border} color={props.color} mainPage={props.mainPage}>
        <Lang
          onClick={() => {
            setShowList((prev) => !prev);
          }}
        >
          <FontAwesomeIcon
            icon={faGlobe}
            style={{ marginRight: props.mainPage ? "0px" : "10px", fontSize: "20px" }}
          />
          <span>{check(language)}</span>
          {!props.mainPage &&
            <>
              {showList ? (
                <FontAwesomeIcon
                  icon={faCaretDown}
                  style={{
                    marginLeft: "10px",
                    fontSize: "20px",
                  }}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faCaretRight}
                  style={{ marginLeft: "5px", fontSize: "20px" }}
                />
              )}
            </>
          }
        </Lang>
        {showList && (
          <PopUp style={{ width: props.mainPage ? 'fit-content' : '165px' }} mainPage={props.mainPage}>
            {languages
              .filter((e) => {
                return e !== language;
              })
              .filter(e => {
                if (shopDetails.fromSpecialLanguagePartner && isSpecialLanguageRoute) {
                  return true;
                } else {
                  return (e !== 'jp' && e !== 'ko' && e !== 'pt' && e !== 'zh' && e !== 'vi');
                }
              })
              .map((element, i) => {
                return (
                  <Options
                    mainPage={props.mainPage}
                    key={i}
                    onClick={() => {
                      changeLanguage(element);
                      setShowList((prev) => !prev);
                    }}
                  >
                    {check(element)}
                  </Options>
                );
              })
            }
          </PopUp>
        )}
      </Container>
    </>
  );
};


const PopUp = styled.div<{ mainPage: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: absolute;
  height: fit-content;
  z-index: 1000;
  background-color: white;
  opacity: 1;
  border: 1px solid black;
  border-radius: 19px;

  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  min-width: ${props => props.mainPage ? "fit-content" : "165px"};

  right: -1px;
  top: 98%;
  padding: 5px 32.5px;
  font-size: ${TEXT_FONTSIZE};
  font-weight: 500;
`;

const Lang = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const Options = styled.div<{ mainPage: boolean }>`
  margin: ${props => props.mainPage ? "5px" : "10px 11px 10px 23px"};
  cursor: pointer;
  color: black;
`;

interface ContainerProps {
  show: any;
  border?: string;
  color?: string;
  mainPage?: boolean;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  font-size: ${TEXT_FONTSIZE};
  font-weight: 500;
  color: ${({ color }) => color || "black"};
  margin: 0px;
  outline: none !important;
  padding: 5px 20px;
  border-radius: 19px;
  height: fit-content;
  display: inline-block;
  cursor: pointer;
  position: relative;
  border: ${({ border }) => border || "1px solid black"};
  width: ${props => props.mainPage ? "95px" : "165px"};
  border-bottom: ${({ show, border }) =>
    show ? "none" : border || "1px solid black"};
  border-bottom-left-radius: ${({ show }) => (show ? "0" : "19px")};
  border-bottom-right-radius: ${({ show }) => (show ? "0" : "19px")};
  :hover {
    color: ${({ color }) => color || "black"};
    box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.3);
  }
`;

export default LanguageList;
